import React, { useState, useEffect } from 'react';
import { X, Plus, Edit3, Trash2, Upload, Download, Eye, Calendar, DollarSign, FileText, Search } from 'lucide-react';
import FileViewerModal from '../common/FileViewerModal';
import { PAYMENT_CHANNELS } from '../../utils/bankAccounts';
import api from '../../services/api';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentItem: any;
  onUpdate: () => void;
}

interface PaymentEntry {
  id?: number;
  payment_amount: number;
  payment_date: string;
  payment_reference: string;
  bank_account: string;
  notes: string;
  receipt_file?: File | null;
}

const PaymentManagementModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  paymentItem,
  onUpdate
}) => {
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [tripDetails, setTripDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [inboundAccounts, setInboundAccounts] = useState<string[]>([]);
  const [editingPayment, setEditingPayment] = useState<number | null>(null);
  const [newPayment, setNewPayment] = useState<PaymentEntry>({
    payment_amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    bank_account: '',
    notes: '',
    receipt_file: null
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [itemDetails, setItemDetails] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [viewerState, setViewerState] = useState<{ isOpen: boolean; fileUrl: string; fileName: string; fileType: 'pdf' | 'image' }>({ 
    isOpen: false, 
    fileUrl: '', 
    fileName: '', 
    fileType: 'image' 
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [floatingBalance, setFloatingBalance] = useState<number>(0);
  const [useFloatingDeposit, setUseFloatingDeposit] = useState(false);
  const [floatingDepositAmount, setFloatingDepositAmount] = useState<number>(0);
  const [receiptPicker, setReceiptPicker] = useState<{ isOpen: boolean; paymentId: number | null }>({
    isOpen: false,
    paymentId: null
  });
  const [receiptCandidates, setReceiptCandidates] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [assigningVendorFileId, setAssigningVendorFileId] = useState<number | null>(null);

  const [currentPaymentItem, setCurrentPaymentItem] = useState(paymentItem);

  const loadInboundAccounts = async () => {
    try {
      const response = await api.get('/treasury/accounts?account_role=outbound');
      if (response.data.success) {
        setInboundAccounts(response.data.data.map((acc: any) => acc.name));
      }
    } catch (error) {
      console.error('Error loading inbound accounts:', error);
    }
  };

  const loadFloatingBalance = async () => {
    if (!paymentItem?.vendor_id) return;
    try {
      const response = await api.get(`/floating-deposits/vendor-balance/${paymentItem.vendor_id}`);
      if (response.data.success) {
        setFloatingBalance(response.data.data.total_balance || 0);
      }
    } catch (error) {
      console.error('Error loading floating balance:', error);
      setFloatingBalance(0);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const loadPaymentDetails = async () => {
    if (!paymentItem?.id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/accounts-v3/payment-item/${paymentItem.id}/details`);
      if (response.data.success) {
        setTripDetails(response.data.data.trip_details || null);
      }
      
      console.log('PaymentItem received in modal:', {
        vendor_id: paymentItem.vendor_id,
        vendor_name: paymentItem.vendor_name,
        itinerary_id: paymentItem.itinerary_id,
        amount: paymentItem.amount,
        id: paymentItem.id,
        status: paymentItem.status
      });
      
      console.log('FULL PaymentItem object:', paymentItem);
      
      // Use vendor_id from table - the table query should be fixed now
      const actualVendorId = paymentItem.vendor_id;
      console.log('Using vendor_id from table:', actualVendorId);
      console.log('Expected vendor_id for Alas Harum should be 148, got:', actualVendorId);
      
      if (!actualVendorId) {
        console.error('CRITICAL: No vendor_id provided');
        alert('Error: Missing vendor ID for payment calculations.');
        return;
      }
      
      if (paymentItem.vendor_name === 'Alas Harum' && actualVendorId !== 148) {
        console.error('CRITICAL MISMATCH: Alas Harum should have vendor_id 148, but got:', actualVendorId);
        alert(`Critical Error: Vendor ID mismatch for Alas Harum. Expected 148, got ${actualVendorId}`);
      }
      
      // Load payment history with receipt file information
      console.log('About to call payment-history API with vendor_id:', actualVendorId, 'itinerary_id:', paymentItem.itinerary_id);
      const paymentHistoryResponse = await api.get(`/accounts-v3/payment-history/${actualVendorId}/${paymentItem.itinerary_id}`);
      if (paymentHistoryResponse.data.success) {
        setPaymentHistory(paymentHistoryResponse.data.data);
        
        // Update current payment item with fresh totals
        const totalPaid = paymentHistoryResponse.data.data.reduce((sum: number, p: any) => sum + (p.payment_amount || 0), 0);
        const outstanding = (paymentItem?.amount || 0) - totalPaid;
        
        let newStatus = 'PENDING_PAYMENT';
        if (totalPaid >= (paymentItem?.amount || 0) && (paymentItem?.amount || 0) > 0) {
          newStatus = 'PAYMENT_COMPLETED';
        } else if (totalPaid > 0 && totalPaid < (paymentItem?.amount || 0)) {
          newStatus = 'PARTIALLY_PAID';
        }
        
        setCurrentPaymentItem({
          ...paymentItem,
          total_paid: totalPaid,
          outstanding_amount: outstanding,
          status: newStatus
        });
        
        console.log('Updated payment item:', {
          totalPaid,
          outstanding,
          newStatus,
          originalAmount: paymentItem?.amount
        });
      }
      
      console.log('About to call invoices API with vendor_id:', actualVendorId, 'itinerary_id:', paymentItem.itinerary_id);
      const invoicesResponse = await api.get(`/accounts-v3/invoices/${actualVendorId}/${paymentItem.itinerary_id}`);
      if (invoicesResponse.data.success) {
        setInvoices(invoicesResponse.data.data);
      }
      
      // Load item details for the Items tab
      console.log('About to call group-details API with vendor_id:', actualVendorId, 'itinerary_id:', paymentItem.itinerary_id, 'status:', paymentItem.status);
      const dueDateTo = paymentItem.due_date || '2030-12-31';
      const itemDetailsResponse = await api.get(`/accounts-v3/group-details/${actualVendorId}/${paymentItem.itinerary_id}/${paymentItem.status}?due_date_to=${dueDateTo}`);
      console.log('Loaded item details:', itemDetailsResponse.data.data);
      setItemDetails(itemDetailsResponse.data.data || []);
    } catch (error) {
      console.error('Error loading payment details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    const normalAmount = newPayment.payment_amount;
    const depositAmount = useFloatingDeposit ? floatingDepositAmount : 0;
    const totalPayment = normalAmount + depositAmount;

    if (totalPayment <= 0) {
      alert('Please enter a payment amount');
      return;
    }

    if (normalAmount > 0 && !newPayment.bank_account) {
      alert('Please select a payment channel for the bank payment');
      return;
    }

    if (depositAmount > floatingBalance) {
      alert(`Floating deposit amount cannot exceed available balance (${formatCurrency(floatingBalance)})`);
      return;
    }
    
    if (submittingPayment) return;
    setSubmittingPayment(true);
    
    try {
      // Step 1: Use floating deposit if applicable
      if (depositAmount > 0) {
        await api.post('/floating-deposits/use', {
          vendor_id: paymentItem.vendor_id,
          itinerary_id: paymentItem.itinerary_id,
          amount: depositAmount,
          payment_reference: newPayment.payment_reference || 'Floating Deposit',
          notes: `Split payment - Floating deposit portion. ${newPayment.notes}`.trim(),
          used_by: 'accounts_v3',
        });
      }

      // Step 2: Record normal payment (includes deposit amount in total for tracking)
      const formData = new FormData();
      formData.append('payment_amount', totalPayment.toString());
      formData.append('payment_date', newPayment.payment_date);
      formData.append('payment_reference', newPayment.payment_reference);
      formData.append('bank_account', depositAmount > 0 && normalAmount === 0 
        ? 'Floating Deposit' 
        : newPayment.bank_account);
      formData.append('notes', depositAmount > 0 
        ? `${newPayment.notes} [Floating Deposit: ${formatCurrency(depositAmount)}${normalAmount > 0 ? `, Bank: ${formatCurrency(normalAmount)}` : ''}]`
        : newPayment.notes);
      
      if (newPayment.receipt_file) {
        formData.append('receipt_file', newPayment.receipt_file);
      }

      const response = await api.post(
        `/accounts-v3/payment-item/${paymentItem.id}/add-payment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        setShowAddForm(false);
        setNewPayment({
          payment_amount: 0,
          payment_date: new Date().toISOString().split('T')[0],
          payment_reference: '',
          bank_account: '',
          notes: '',
          receipt_file: null
        });
        setUseFloatingDeposit(false);
        setFloatingDepositAmount(0);
        
        // Refresh from server to get authoritative data (no optimistic update to avoid duplicates)
        await loadPaymentDetails();
        await loadFloatingBalance();
        onUpdate();
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Error adding payment. Please try again.');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handleEditPayment = async (paymentId: number, updatedData: any) => {
    try {
      const response = await api.put(`/accounts-v3/payment/${paymentId}`, updatedData);
      if (response.data.success) {
        setEditingPayment(null);
        // Wait for backend processing then refresh
        setTimeout(async () => {
          await loadPaymentDetails();
          onUpdate();
        }, 500);
      }
    } catch (error) {
      console.error('Error updating payment:', error);
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      const response = await api.delete(`/accounts-v3/payment/${paymentId}`);
      if (response.data.success) {
        // Wait for backend processing then refresh
        setTimeout(async () => {
          await loadPaymentDetails();
          onUpdate();
        }, 500);
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const handleViewReceipt = async (payment: any) => {
    // First try to use the direct S3 URL from payment history
    if (payment.receipt_s3_url) {
      handleViewFile(payment.receipt_s3_url, payment.receipt_filename);
      return;
    }
    
    // Check if we have invoices data with presigned URLs
    const receiptFile = invoices.find(invoice => 
      invoice.filename === payment.receipt_filename || 
      invoice.s3_key === payment.receipt_s3_key
    );
    
    if (receiptFile && receiptFile.url) {
      // Use the presigned URL from invoices data
      handleViewFile(receiptFile.url, receiptFile.filename);
    } else {
      // Fallback to old method
      try {
        const response = await api.get(`/file/presigned-url/${payment.receipt_s3_key}`);
        if (response.data.success) {
          handleViewFile(response.data.data.url, payment.receipt_filename);
        }
      } catch (error) {
        console.error('Error getting receipt URL:', error);
        alert('Unable to view receipt file. The file may not exist or be accessible.');
      }
    }
  };

  const handleViewFile = (fileUrl: string, fileName: string) => {
    console.log('Attempting to view file:', { fileUrl, fileName });
    const fileExt = fileName.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt || '')) {
      setViewerState({ isOpen: true, fileUrl, fileName, fileType: 'image' });
    } else if (fileExt === 'pdf') {
      setViewerState({ isOpen: true, fileUrl, fileName, fileType: 'pdf' });
    } else {
      // For other file types, try direct download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();
    }
  };

  const openReceiptPicker = async (paymentId: number) => {
    setReceiptPicker({ isOpen: true, paymentId });
    setLoadingCandidates(true);
    try {
      const response = await api.get(`/accounts-v3/payment/${paymentId}/receipt-candidates`);
      if (response.data.success) {
        setReceiptCandidates(response.data.data);
      }
    } catch (error) {
      console.error('Error loading receipt candidates:', error);
      alert('Error loading candidate receipts. Please try again.');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const closeReceiptPicker = () => {
    setReceiptPicker({ isOpen: false, paymentId: null });
    setReceiptCandidates([]);
  };

  const handleAssignReceipt = async (vendorFileId: number) => {
    if (!receiptPicker.paymentId) return;
    setAssigningVendorFileId(vendorFileId);
    try {
      const response = await api.post(`/accounts-v3/payment/${receiptPicker.paymentId}/assign-receipt`, {
        vendor_file_id: vendorFileId
      });
      if (response.data.success) {
        closeReceiptPicker();
        await loadPaymentDetails();
      }
    } catch (error: any) {
      console.error('Error assigning receipt:', error);
      alert(error?.response?.data?.detail || 'Error assigning receipt. Please try again.');
    } finally {
      setAssigningVendorFileId(null);
    }
  };

  useEffect(() => {
    if (isOpen && paymentItem) {
      setCurrentPaymentItem(paymentItem);
      loadInboundAccounts();
      loadPaymentDetails();
      loadFloatingBalance();
      setUseFloatingDeposit(false);
      setFloatingDepositAmount(0);
      // Set payment amount explicitly - no fallbacks
      if (paymentItem.outstanding_amount !== undefined) {
        setNewPayment(prev => ({ ...prev, payment_amount: paymentItem.outstanding_amount }));
      } else if (paymentItem.amount !== undefined) {
        setNewPayment(prev => ({ ...prev, payment_amount: paymentItem.amount }));
      }
    }
  }, [isOpen, paymentItem]);

  if (!isOpen) return null;

  const totalPaid = paymentHistory.reduce((sum, p) => sum + (p.payment_amount || 0), 0);
  const outstanding = (currentPaymentItem?.amount || 0) - totalPaid;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl h-full sm:h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 sm:p-6 border-b gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-bold truncate">Payment Management - WH_{currentPaymentItem?.itinerary_id}</h2>
            <p className="text-sm sm:text-base text-gray-600 truncate">
              {currentPaymentItem?.vendor_name}
              {currentPaymentItem?.booking_reference && 
                ` | ${currentPaymentItem.booking_reference}`
              } | {currentPaymentItem?.guest_name}
            </p>
            {tripDetails && (
              <div className="text-xs sm:text-sm text-gray-500 mt-1 flex flex-wrap gap-3">
                {tripDetails.travel_date && (
                  <span>📅 {tripDetails.travel_date}</span>
                )}
                {tripDetails.no_of_days && (
                  <span>🗓️ {tripDetails.no_of_days} days</span>
                )}
                {tripDetails.no_of_pax && (
                  <span>👥 {tripDetails.no_of_pax} pax</span>
                )}
                {tripDetails.no_of_children && tripDetails.no_of_children > 0 && (
                  <span>👶 {tripDetails.no_of_children} kids{tripDetails.kids_ages?.length > 0 ? ` (${tripDetails.kids_ages.join(', ')} yrs)` : ''}</span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Payment Summary Banner */}
        <div className={`p-3 sm:p-4 ${
          currentPaymentItem?.status === 'PAYMENT_COMPLETED' ? 'bg-green-50' : 
          currentPaymentItem?.status === 'PARTIALLY_PAID' ? 'bg-yellow-50' : 
          'bg-red-50'
        }`}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div><strong>Total Amount:</strong> {formatCurrency(currentPaymentItem?.amount || 0)}</div>
            <div><strong>Total Paid:</strong> {formatCurrency(totalPaid)}</div>
            <div><strong>Outstanding:</strong> {formatCurrency(outstanding)}</div>
            <div><strong>Status:</strong> <span className={`font-semibold ${
              currentPaymentItem?.status === 'PAYMENT_COMPLETED' ? 'text-green-600' : 
              currentPaymentItem?.status === 'PARTIALLY_PAID' ? 'text-yellow-600' : 
              'text-red-600'
            }`}>{currentPaymentItem?.status?.replace('_', ' ')}</span></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'overview' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'items' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('items')}
          >
            📋 Item Details
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'payments' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('payments')}
          >
            💳 Payment History
          </button>
          <button
            className={`px-3 sm:px-6 py-2 sm:py-3 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'invoices' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500'
            }`}
            onClick={() => setActiveTab('invoices')}
          >
            📄 Invoices
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div>
              {/* Quick Summary */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-blue-900 mb-2">Summary</h3>
                <p className="text-blue-700">
                  <strong>{paymentItem?.item_count || 1} items</strong> • 
                  Categories: {paymentItem?.payment_categories || paymentItem?.payment_category} • 
                  Due: {paymentItem?.due_date ? new Date(paymentItem.due_date).toLocaleDateString('en-GB') : 'N/A'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="space-y-4">
              {itemDetails.length > 0 ? (
                (() => {
                  // Group items by item_type
                  const groupedItems = itemDetails.reduce((acc: { [key: string]: any[] }, item: any) => {
                    const type = item.item_type || 'other';
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(item);
                    return acc;
                  }, {} as { [key: string]: any[] });
                  
                  // Get driver name from booking_reference if vendor_id is 840
                  const driverName = itemDetails.find((item: any) => item.vendor_id === 840)?.booking_reference;
                  
                  return Object.entries(groupedItems).map(([itemType, items]: [string, any[]]) => {
                    const typeTotal = items.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
                    const typeLabel = itemType === 'driver_paid' ? 'Driver Paid Activities' : 
                                     itemType === 'transportation' ? 'Transportation' : 
                                     itemType.charAt(0).toUpperCase() + itemType.slice(1);
                    
                    return (
                      <div key={itemType} className="bg-white border border-gray-200 rounded-lg">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <div className="flex justify-between items-center">
                            <h4 className="font-medium text-gray-900">
                              {typeLabel} ({items.length} items)
                              {itemType === 'driver_paid' && driverName && (
                                <span className="ml-2 text-sm text-blue-600">• Driver: {driverName}</span>
                              )}
                            </h4>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(typeTotal)}
                            </span>
                          </div>
                        </div>
                        <div className="p-4 space-y-3">
                          {items.map((item: any, index: number) => (
                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded border">
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">{item.item_name}</h5>
                                <p className="text-sm text-gray-600 mt-1">{item.description || item.item_type}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  item.status === 'BOOKED' || item.status === 'NOT_BOOKED'
                                    ? 'bg-red-100 text-red-800' 
                                    : item.status === 'PENDING_PAYMENT'
                                    ? 'bg-blue-100 text-blue-800'
                                    : item.status === 'PAYMENT_COMPLETED'
                                    ? 'bg-green-100 text-green-800'
                                    : item.status === 'PARTIALLY_PAID'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {(item.status === 'BOOKED' || item.status === 'NOT_BOOKED') && (
                                    <span className="text-red-600">⚠️</span>
                                  )}
                                  {item.status === 'BOOKED' ? 'PENDING INVOICE' : item.status.replace('_', ' ')}
                                </span>
                                <p className="text-lg font-semibold text-gray-900">
                                  {formatCurrency(item.amount)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()
              ) : (
                paymentItem?.item_names && (
                  <div className="space-y-3">
                    {paymentItem.item_names.split(' | ').map((itemName: string, index: number) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{itemName}</p>
                            <p className="text-sm text-gray-600">{paymentItem.payment_categories}</p>
                          </div>
                          <p className="text-sm text-gray-500">Part of group total</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {activeTab === 'payments' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Payment History</h3>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Payment
                </button>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading payment history...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentHistory.map((payment, index) => (
                    <div key={payment.id || index} className="bg-white border border-gray-200 rounded-lg p-4">
                      {editingPayment === payment.id ? (
                        <EditPaymentForm
                          payment={payment}
                          onSave={(data) => handleEditPayment(payment.id, data)}
                          onCancel={() => setEditingPayment(null)}
                          inboundAccounts={inboundAccounts}
                        />
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {formatCurrency(payment.payment_amount)}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {new Date(payment.payment_date).toLocaleDateString('en-GB')}
                                </p>
                                {payment.bank_account && (
                                  <p className="text-sm text-blue-600">
                                    {payment.bank_account}
                                  </p>
                                )}
                              </div>
                              {payment.payment_reference && (
                                <div>
                                  <p className="text-sm text-gray-600">Ref: {payment.payment_reference}</p>
                                </div>
                              )}
                              {payment.notes && (
                                <div>
                                  <p className="text-sm text-gray-600">{payment.notes}</p>
                                </div>
                              )}
                              {payment.receipt_filename ? (
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm text-blue-600">Receipt attached</span>
                                  <button
                                    onClick={() => handleViewReceipt(payment)}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                    title="View Receipt"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-400">No receipt linked</span>
                                  <button
                                    onClick={() => openReceiptPicker(payment.id)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                                    title="Find a previously uploaded receipt for this vendor/trip"
                                  >
                                    <Search className="h-3 w-3" />
                                    Select existing receipt
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => setEditingPayment(payment.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {paymentHistory.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No payments recorded yet</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'invoices' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Files</h3>
              {invoices.length > 0 ? (
                <div className="space-y-3">
                  {invoices.map((invoice, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                          <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{invoice.filename}</p>
                            <p className="text-xs sm:text-sm text-gray-600">
                              {invoice.file_type} • {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('en-GB') : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleViewFile(invoice.url, invoice.filename)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="View Invoice"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewFile(invoice.url, invoice.filename)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Download Invoice"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No invoice files uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {/* Add Payment Form - shown on payments tab */}
          {activeTab === 'payments' && showAddForm && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-4">Add New Payment</h4>

              {/* Floating Deposit Section */}
              {floatingBalance > 0 && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">💰 Floating Deposit Available</p>
                      <p className="text-lg font-bold text-green-700">{formatCurrency(floatingBalance)}</p>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useFloatingDeposit}
                        onChange={(e) => {
                          setUseFloatingDeposit(e.target.checked);
                          if (!e.target.checked) setFloatingDepositAmount(0);
                        }}
                        className="rounded border-green-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-green-800">Use Floating Deposit</span>
                    </label>
                  </div>
                  {useFloatingDeposit && (
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-green-700 mb-1">Amount from Deposit</label>
                        <input
                          type="number"
                          value={floatingDepositAmount}
                          onChange={(e) => {
                            const val = Math.min(parseFloat(e.target.value) || 0, floatingBalance);
                            setFloatingDepositAmount(val);
                          }}
                          max={floatingBalance}
                          className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                          placeholder={`Max: ${formatCurrency(floatingBalance)}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFloatingDepositAmount(Math.min(floatingBalance, currentPaymentItem?.outstanding_amount || paymentItem?.amount || 0))}
                        className="mt-5 px-3 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Use Max
                      </button>
                    </div>
                  )}
                  {useFloatingDeposit && floatingDepositAmount > 0 && (
                    <p className="mt-2 text-xs text-green-600">
                      Remaining to pay via bank: {formatCurrency(Math.max(0, (currentPaymentItem?.outstanding_amount || paymentItem?.amount || 0) - floatingDepositAmount))}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {useFloatingDeposit && floatingDepositAmount > 0 ? 'Bank Payment Amount' : 'Payment Amount'}
                  </label>
                  <input
                    type="number"
                    value={newPayment.payment_amount}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      payment_amount: parseFloat(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    value={newPayment.payment_date}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      payment_date: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Channel
                  </label>
                  <select
                    value={newPayment.bank_account}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      bank_account: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select payment channel *</option>
                    {inboundAccounts.map((account) => (
                      <option key={account} value={account}>
                        {account}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reference
                  </label>
                  <input
                    type="text"
                    value={newPayment.payment_reference}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      payment_reference: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Bank transfer ref, check number, etc."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt File
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      receipt_file: e.target.files?.[0] || null
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {newPayment.receipt_file && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                      <FileText className="h-4 w-4" />
                      <span>{newPayment.receipt_file.name}</span>
                      <button
                        onClick={() => setNewPayment({...newPayment, receipt_file: null})}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({
                      ...newPayment,
                      notes: e.target.value
                    })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddPayment}
                  disabled={submittingPayment}
                  className={`px-4 py-2 text-white rounded-lg ${submittingPayment ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {submittingPayment ? 'Adding...' : 'Add Payment'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <FileViewerModal
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        fileUrl={viewerState.fileUrl}
        fileName={viewerState.fileName}
        fileType={viewerState.fileType}
      />

      {receiptPicker.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Select existing receipt</h3>
                <p className="text-sm text-gray-500">
                  Only receipts already uploaded for this vendor/trip that aren't linked to another payment are shown.
                  Open each one to verify it before assigning — don't guess from the thumbnail alone.
                </p>
              </div>
              <button onClick={closeReceiptPicker} className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              {loadingCandidates ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading candidate receipts...</p>
                </div>
              ) : receiptCandidates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No unlinked receipts found for this vendor/trip.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {receiptCandidates.map((candidate) => (
                    <div
                      key={candidate.vendor_file_id}
                      className="flex items-center gap-3 border border-gray-200 rounded-lg p-3"
                    >
                      <button
                        onClick={() => candidate.url && handleViewFile(candidate.url, candidate.filename)}
                        className="flex-shrink-0 w-16 h-16 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center"
                        title="Click to enlarge and verify"
                      >
                        {candidate.url && candidate.filename?.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/) ? (
                          <img src={candidate.url} alt={candidate.filename} className="w-full h-full object-cover" />
                        ) : (
                          <FileText className="h-6 w-6 text-gray-400" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{candidate.filename}</p>
                        <p className="text-xs text-gray-500">
                          Uploaded {candidate.uploaded_at ? new Date(candidate.uploaded_at).toLocaleString('en-GB') : 'N/A'}
                        </p>
                        <button
                          onClick={() => candidate.url && handleViewFile(candidate.url, candidate.filename)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Click to enlarge and verify
                        </button>
                      </div>

                      <button
                        onClick={() => handleAssignReceipt(candidate.vendor_file_id)}
                        disabled={assigningVendorFileId === candidate.vendor_file_id}
                        className="flex-shrink-0 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                      >
                        {assigningVendorFileId === candidate.vendor_file_id ? 'Assigning...' : 'Use this receipt'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Edit Payment Form Component
const EditPaymentForm: React.FC<{
  payment: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  inboundAccounts: string[];
}> = ({ payment, onSave, onCancel, inboundAccounts }) => {
  const [formData, setFormData] = useState({
    payment_amount: payment.payment_amount,
    payment_date: payment.payment_date,
    payment_reference: payment.payment_reference || '',
    bank_account: payment.bank_account || '',
    notes: payment.notes || ''
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
          <input
            type="number"
            value={formData.payment_amount}
            onChange={(e) => setFormData({
              ...formData,
              payment_amount: parseFloat(e.target.value) || 0
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={formData.payment_date}
            onChange={(e) => setFormData({
              ...formData,
              payment_date: e.target.value
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Channel *</label>
          <select
            value={formData.bank_account}
            onChange={(e) => setFormData({
              ...formData,
              bank_account: e.target.value
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select payment channel</option>
            {inboundAccounts.map((account) => (
              <option key={account} value={account}>
                {account}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
          <input
            type="text"
            value={formData.payment_reference}
            onChange={(e) => setFormData({
              ...formData,
              payment_reference: e.target.value
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <input
            type="text"
            value={formData.notes}
            onChange={(e) => setFormData({
              ...formData,
              notes: e.target.value
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(formData)}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default PaymentManagementModal;